import {Component, OnInit} from '@angular/core';
import {FormControl} from '@angular/forms';
import {BehaviorSubject} from 'rxjs';
import {finalize} from 'rxjs/operators';
import {MatTableDataSource} from '@angular/material/table';
import {DialogService} from '../../../services/dialog.service';
import {Router} from '@angular/router';
import {SearchService} from '../../../services/search.service';
import {RetainedDocument} from '../../../models/retained-document.model';
import {environment} from '../../../../environments/environment';
import {UserService} from '../../../services/user.service';
import {MatDialog} from '@angular/material/dialog';
import {
  RetainedDocumentAttachmentsDialogComponent
} from '../../../components/retained-document-attachments-dialog/retained-document-attachments-dialog.component';
import {MatIconRegistry} from '@angular/material/icon';
import {DomSanitizer} from '@angular/platform-browser';
import {extractErrorMessage} from '../../../utils/error-utils';

@Component({
  selector: 'app-retained-documents',
  templateUrl: './retained-documents.component.html',
  styleUrls: ['./retained-documents.component.scss']
})
export class RetainedDocumentsComponent implements OnInit {

  public searchForm = new FormControl();
  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();
  public dataSource: MatTableDataSource<RetainedDocument>;
  public columns = ['title', 'library', 'class', 'creator', 'download', 'link'];
  public totalCount = 0;
  public pageSize = 20;
  public pageSizeOptions = [10, 20, 30];
  private pageTokens: string[] = [null]; // token history: index 0 = first page (null token)
  private currentPageIndex = 0;
  private currentQuery: string = null;
  private isLoading = false;

  constructor(protected searchService: SearchService,
              protected userService: UserService,
              private dialog: MatDialog,
              private iconRegistry: MatIconRegistry,
              private sanitizer: DomSanitizer,
              private dialogService: DialogService,
              protected router: Router) {
    iconRegistry.addSvgIcon('see_attachments', sanitizer.bypassSecurityTrustResourceUrl('assets/img/see-attachments.svg'));
  }

  ngOnInit(): void {
    this.loading$.subscribe(event => this.isLoading = event);
    this.dataSource = new MatTableDataSource<RetainedDocument>();
  }

  search(): void {
    if (!this.isLoading && this.isSearchQueryCompatible()) {
      this.currentQuery = decodeURIComponent(this.searchForm.value);
      this.pageTokens = [null];
      this.currentPageIndex = 0;
      this.loadPage();
    }
  }

  loadPage(): void {
    this.loadingSubject.next(true);
    const token = this.pageTokens[this.currentPageIndex];
    this.searchService.searchRetainedDocuments(this.currentQuery, this.pageSize, token).pipe(
      finalize(() => this.loadingSubject.next(false))
    ).subscribe({
      next: (resp) => {
        this.dataSource.data = resp.content;
        this.totalCount = resp.count;
        // Store next page token if we don't already have it
        if (resp.pageToken && this.pageTokens.length <= this.currentPageIndex + 1) {
          this.pageTokens.push(resp.pageToken);
        }
      },
      error: (error) => {
        this.dialogService.error(extractErrorMessage(error));
      }
    });
  }

  onPageChange(event: any): void {
    if (event.pageSize !== this.pageSize) {
      // Page size changed — reset to first page
      this.pageSize = event.pageSize;
      this.pageTokens = [null];
      this.currentPageIndex = 0;
    } else {
      this.currentPageIndex = event.pageIndex;
    }
    this.loadPage();
  }

  getDocumentUrl(document: RetainedDocument): string {
    const aodocsUrl = environment.aodocsUrl;
    const domain = this.userService.user.domain;
    return aodocsUrl + '?aodocs-domain=' + domain + '#Menu_viewDoc/LibraryId_' + document.libraryId + '/DocumentId_' + document.documentId;
  }

  displayAttachments(element: RetainedDocument): void {
    this.dialog.open(RetainedDocumentAttachmentsDialogComponent, {
      data: {document: element},
      width: '500px'
    });
  }

  hasNoAttachments(element: RetainedDocument): boolean {
    return !element.attachments || element.attachments.length === 0;
  }

  isSearchQueryCompatible(): boolean {
    return this.searchForm.value && this.searchForm.value.length >= 3;
  }

  displayedColumns(): string[] {
    return this.columns;
  }
}
